/**
 * RulesIntroductionCard Component
 *
 * Promotional card for the JotAudit Rules tab introducing the rules audit feature.
 * Displays a "NEW FEATURE" badge, description, action buttons, and an embedded video player.
 * Behind the guide-links gate, a guide section control follows the title and stands in for
 * the Front help-center button.
 */

import React, { useEffect, useRef, useState } from 'react';
import { GUIDE_LINKS_GATE, GUIDE_SURFACES, GuideLink } from '@features/knowledge-base/links';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Chip, Collapse, IconButton, Paper, styled, Typography } from '@mui/material';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { publicAsset } from '@utils/publicAsset';

import { dismissCard } from '../../store/dismissalsSlice';
import { selectDismissedCardIds } from '../../store/selectors';

/** The demo player fills its framed column; static sizing lives in styles, not inline. */
const DemoVideo = styled('video')({ width: '100%', aspectRatio: '16 / 9', display: 'block' });

interface RulesIntroductionCardProps {
  /** Callback when Try It Out is clicked */
  onTryItOut?: () => void;
  /** URL for Learn More documentation */
  learnMoreUrl?: string;
}

const CARD_ID = 'rules-introduction-card'; // Unique ID for this card

export const RulesIntroductionCard: React.FC<RulesIntroductionCardProps> = ({
  onTryItOut,
  learnMoreUrl = 'https://jotpsych.frontkb.com/en/articles/10308289',
}) => {
  const dispatch = useAppDispatch();
  const { jotauditDashboard } = useEventTracking();
  // Read once: while open, the guide link stands in for the Front help-center button.
  const guideLinksEnabled = useFeatureGate(GUIDE_LINKS_GATE);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Check if card is dismissed (from Redux persist)
  const dismissedIds = useAppSelector(selectDismissedCardIds);
  const isDismissedInStore = dismissedIds.includes(CARD_ID);

  // Local animation state (separate from Redux for smooth animation)
  const [isVisible, setIsVisible] = useState(!isDismissedInStore);

  // Sync with Redux state changes (e.g., from other tabs)
  useEffect(() => {
    if (isDismissedInStore && isVisible) {
      setIsVisible(false);
    }
  }, [isDismissedInStore, isVisible]);

  // Track view on mount (only if not dismissed)
  useEffect(() => {
    if (!isDismissedInStore) {
      jotauditDashboard.rulesIntroViewed({ page: 'rules_tab' });
    }
  }, [jotauditDashboard, isDismissedInStore]);

  const handleTryItOut = () => {
    jotauditDashboard.rulesIntroTryItClicked({ source: 'intro_card' });
    onTryItOut?.();
  };

  const handleLearnMore = () => {
    jotauditDashboard.rulesIntroLearnMoreClicked({ url: learnMoreUrl });
    window.open(learnMoreUrl, '_blank', 'noopener,noreferrer');
  };

  const handleVideoPlay = () => {
    const videoTime = videoRef.current?.currentTime ?? 0;
    jotauditDashboard.rulesIntroVideoPlayed({ video_time: videoTime });
  };

  const handleDismiss = () => {
    // Track dismiss event
    jotauditDashboard.rulesIntroDismissed({ card_id: CARD_ID });

    // Trigger exit animation first
    setIsVisible(false);

    // After animation completes, persist to Redux
    // Collapse animation is ~300ms by default
    setTimeout(() => {
      dispatch(dismissCard(CARD_ID));
    }, 350);
  };

  const title = (
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        fontSize: '20px',
        color: 'text.primary',
      }}
    >
      Auditing Notes with Rules
    </Typography>
  );

  return (
    <Collapse in={isVisible} unmountOnExit>
      <Paper
        elevation={0}
        sx={{
          mt: { xs: -2, md: 0 },
          px: 3,
          pb: 3,
          mb: 3,
          maxWidth: 1024,
          mx: 'auto',
          position: 'relative', // For absolute positioning of close button on desktop
        }}
      >
        {/* Close Button - Desktop Only (absolute positioned outside) */}
        <IconButton
          onClick={handleDismiss}
          aria-label="Dismiss introduction card"
          sx={{
            display: { xs: 'none', md: 'flex' }, // Hide on mobile
            position: 'absolute',
            top: 0,
            right: -16,
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
            gap: 3,
            alignItems: 'center',
          }}
        >
          {/* Left Column: Badge, Title, Description, Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* NEW FEATURE Badge with Close Button (mobile) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Chip
                label="NEW FEATURE"
                sx={{
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                  color: '#1976d2',
                  fontWeight: 700,
                  fontSize: '9px',
                  height: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  '.MuiChip-label': {
                    px: 1,
                  },
                }}
              />
              {/* Close Button - Mobile Only (inline with badge) */}
              <IconButton
                onClick={handleDismiss}
                aria-label="Dismiss introduction card"
                size="small"
                sx={{
                  display: { xs: 'flex', md: 'none' }, // Show on mobile only
                  color: 'text.secondary',
                  ml: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Title and Description */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* The row wrapper exists only on the gated path, so the ungated card keeps its markup. */}
              {guideLinksEnabled ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {title}
                  <GuideLink
                    surfaceId={GUIDE_SURFACES.JOTAUDIT_RULES_CARD}
                    shape="section-control"
                    topic="audit"
                  />
                </Box>
              ) : (
                title
              )}
              <Typography
                variant="body2"
                sx={{
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#8e839c',
                }}
              >
                We scan your note using payer documentation rules to support reimbursement. Rules
                check for required information and consistency. You can add or edit your own rules
                anytime.
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={handleTryItOut}
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  fontSize: '14px',
                  px: 2,
                  py: 0.75,
                  borderRadius: 1,
                }}
              >
                Try It Out
              </Button>
              {!guideLinksEnabled && (
                <Button
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                  onClick={handleLearnMore}
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    fontSize: '14px',
                    px: 2,
                    py: 0.75,
                    borderRadius: 1,
                  }}
                >
                  Learn More
                </Button>
              )}
            </Box>
          </Box>

          {/* Right Column: Video Player */}
          <Box
            sx={{
              bgcolor: '#1f0d71',
              border: '10px solid #3921a4',
              borderRadius: '20px',
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
            }}
          >
            {/* The demo has no captions track; the a11y rule does not see the styled element, so the gap is recorded here. */}
            <DemoVideo
              ref={videoRef}
              controls
              onPlay={handleVideoPlay}
              aria-label="JotAudit rules feature demonstration video"
            >
              <source
                src={publicAsset('/videos/jotaudit/JotAudit_Admin_Dashboard.mp4')}
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </DemoVideo>
          </Box>
        </Box>
      </Paper>
    </Collapse>
  );
};
